/**
 * ✅ Tareas — 3 vistas (Lista / Calendario / Sin horario):
 *   - Lista: por sección, separando con horario vs generales
 *   - Calendario: vista mensual estilo iPhone Calendar con eventos por día
 *   - Sin horario: tareas generales agrupadas
 *
 * Edición completa: hora inicio, hora fin, sección, prioridad, sin hora específica.
 */
import { h, apiClient, pill, emptyState, btn, openModal, openConfirm, toast, sectionHeader, statTile, openQuickTaskModal } from './_common.js';

const SECCIONES = [
  { id: 'personal',    label: 'Personal',     emoji: '🌟', color: 'purple' },
  { id: 'autosell',    label: 'Autosell',     emoji: '🚗', color: 'red' },
  { id: 'inmuebles',   label: 'Inmuebles',    emoji: '🏠', color: 'blue' },
  { id: 'carlos-ai',   label: 'CARLOS.AI',    emoji: '🤖', color: 'green' },
  { id: 'coco-doree',  label: 'COCO/DORÉE',   emoji: '🛍️', color: 'gold' },
  { id: 'suiza-go',    label: 'Suiza-go',     emoji: '🇨🇭', color: 'gold' },
  { id: 'salud',       label: 'Salud',        emoji: '💪', color: 'red' },
];
// --- Secciones personalizadas (se guardan en localStorage y se auto-descubren de las tareas de la BD,
// asi en cualquier dispositivo aparecen en cuanto tienen una tarea) ---
let _extraSecs = [];
try { _extraSecs = JSON.parse(localStorage.getItem('carlos_secciones_custom') || '[]'); } catch { _extraSecs = []; }
function getSecs() {
  const seen = new Set(SECCIONES.map((s) => s.id));
  const list = SECCIONES.slice();
  for (const e of _extraSecs) if (e && e.id && !seen.has(e.id)) { seen.add(e.id); list.push(e); }
  return list;
}
function registerSecsFromTasks(tasks) {
  const seen = new Set(getSecs().map((s) => s.id));
  let changed = false;
  for (const t of tasks || []) {
    const sid = t.seccion;
    if (sid && !seen.has(sid)) {
      seen.add(sid);
      _extraSecs.push({ id: sid, label: sid.charAt(0).toUpperCase() + sid.slice(1).replace(/-/g, ' '), emoji: '📌', color: 'gold' });
      changed = true;
    }
  }
  if (changed) { try { localStorage.setItem('carlos_secciones_custom', JSON.stringify(_extraSecs)); } catch {} }
}
function openNewSectionModal(root) {
  openModal({
    title: 'Nueva sección',
    icon: '📁',
    fields: [
      { name: 'nombre', label: 'Nombre de la sección/proyecto', required: true, placeholder: 'p.ej. Coco Summer' },
      { name: 'emoji', label: 'Emoji (opcional)', placeholder: '📌' },
    ],
    onSubmit: async (vals) => {
      const nombre = (vals.nombre || '').trim();
      if (!nombre) return;
      const id = nombre.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30) || ('sec-' + Date.now().toString(36));
      if (!getSecs().some((x) => x.id === id)) {
        _extraSecs.push({ id, label: nombre, emoji: (vals.emoji || '📌').trim() || '📌', color: 'gold' });
        try { localStorage.setItem('carlos_secciones_custom', JSON.stringify(_extraSecs)); } catch {}
      }
      toast('Sección creada · añade su primera tarea', 'success');
      openQuickTaskModal(id, () => renderTareas(root));
    },
  });
}

const PRIO_COLORS = {
  critica: 'bg-red-500/20 text-red-200 border-red-500/50',
  alta: 'bg-orange-500/20 text-orange-200 border-orange-500/50',
  media: 'bg-yellow-500/20 text-yellow-200 border-yellow-500/50',
  baja: 'bg-base-600 text-ink-200 border-base-600',
};
const PRIO_LABELS = { critica: 'P0', alta: 'P1', media: 'P2', baja: 'P3' };

let _selected = new Set();
let _view = 'list';          // 'list' | 'calendar' | 'general'
let _calSource = 'all';      // 'all' | 'personal' | 'ai' — qué calendario muestra
let _filter = { seccion: '', status: 'all', q: '' };
let _calMonth = new Date(); _calMonth.setDate(1);

export async function renderTareas(root) {
  // CLEAR DEFINITIVO para evitar duplicación
  const _scrollY = window.scrollY || 0;
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('✅', 'Tareas', 'Lista · Calendario · Sin horario', [
    btn('+ Nueva tarea', () => openTaskModal(null, () => renderTareas(root))),
    btn('📁 + Sección', () => openNewSectionModal(root), { variant: 'secondary' }),
    btn('🔗 Suscribir iPhone', () => openCalendarSubscribeModal(), { variant: 'secondary' }),
  ]));

  // View switcher
  wrap.appendChild(h('div', { class: 'flex gap-1 border-b border-base-700 mb-5 overflow-x-auto' }, [
    viewTab('list', '📋 Lista', root),
    viewTab('calendar', '📅 Calendario', root),
    viewTab('general', '📌 Sin horario', root),
  ]));

  // Stats globales
  wrap.appendChild(h('div', { id: 'tareas-global-stats', class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5' }));

  // Contenedor que cambia según view
  wrap.appendChild(h('div', { id: 'tareas-view-content' }));

  root.appendChild(wrap);
  await renderActiveView(wrap, root);
  requestAnimationFrame(() => { window.scrollTo({ top: _scrollY, behavior: 'instant' }); });
}

function viewTab(id, label, root) {
  const active = _view === id;
  return h('button', {
    class: `px-4 py-2 text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${active ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100'}`,
    onclick: () => { _view = id; renderTareas(root); },
  }, label);
}

async function renderActiveView(wrap, root) {
  // Cargar siempre todas las tareas
  const res = await apiClient.get('/api/v1/tasks?limit=500').catch(() => ({ data: [] }));
  const all = res.data || [];

  // Stats globales
  const statsBox = wrap.querySelector('#tareas-global-stats');
  statsBox.innerHTML = '';
  const totalPending = all.filter((t) => t.status !== 'done').length;
  const totalScheduled = all.filter((t) => hasSchedule(t) && t.status !== 'done').length;
  const totalGeneral = all.filter((t) => !hasSchedule(t) && t.status !== 'done').length;
  const totalCritical = all.filter((t) => t.priority === 'critica' && t.status !== 'done').length;
  statsBox.appendChild(statTile({ label: 'Pendientes', value: totalPending, emoji: '🔥', color: 'gold' }));
  statsBox.appendChild(statTile({ label: 'Con horario', value: totalScheduled, emoji: '🕐', color: 'blue' }));
  statsBox.appendChild(statTile({ label: 'Generales', value: totalGeneral, emoji: '📌', color: 'purple' }));
  statsBox.appendChild(statTile({ label: 'Críticas', value: totalCritical, emoji: '🚨', color: 'red' }));

  const c = wrap.querySelector('#tareas-view-content');
  c.innerHTML = '';

  // Filtro de fuente: Personal vs IA vs Todo (aplica a vista calendario y lista)
  c.appendChild(h('div', { class: 'flex gap-2 mb-3' }, [
    calSourceBtn('all',      'Todo',                root),
    calSourceBtn('personal', '📓 Personal',          root),
    calSourceBtn('ai',       '🤖 Calendario IA',     root),
  ]));

  // Filtra tareas según la fuente
  const isAiTask = (t) => Array.isArray(t.tags) && t.tags.includes('ia-calendar');
  const filteredSource = _calSource === 'personal' ? all.filter((t) => !isAiTask(t))
                       : _calSource === 'ai'       ? all.filter((t) => isAiTask(t))
                       :                              all;

  if (_view === 'list') renderListView(c, filteredSource, root);
  else if (_view === 'calendar') renderCalendarView(c, filteredSource, root);
  else renderGeneralView(c, filteredSource, root);
}

function calSourceBtn(id, label, root) {
  const active = _calSource === id;
  return h('button', {
    class: `text-xs px-3 py-1.5 rounded-lg transition border ${active ? 'bg-gold-400/20 border-gold-400/40 text-gold-300' : 'bg-base-800 border-base-700 text-ink-300 hover:text-ink-100'}`,
    onclick: () => { _calSource = id; renderTareas(root); },
  }, label);
}

async function openCalendarSubscribeModal() {
  let r;
  try { r = await apiClient.get('/api/v1/calendar/share-tokens'); }
  catch (e) {
    toast('Error obteniendo tokens: ' + (e?.message || ''), 'error');
    return;
  }
  const { openModal } = await import('./_common.js');
  openModal({
    title: '🔗 Suscribir calendarios en tu iPhone',
    subtitle: 'Copia el URL y pégalo en iPhone → Ajustes → Calendario → Cuentas → Añadir cuenta → Otra → Añadir calendario suscrito',
    size: 'lg',
    fields: [],
    submitLabel: 'Cerrar',
    onSubmit: async () => {},
  });
  // Después de mostrar modal abrimos modal personalizado:
  setTimeout(() => {
    const overlay = document.querySelector('.fixed.inset-0.bg-black\\/70');
    if (!overlay) return;
    const body = overlay.querySelector('form');
    if (!body) return;
    body.innerHTML = '';
    const block = (titulo, urls, color) => {
      const wrap = h('div', { class: `border border-${color}-400/30 bg-${color}-400/5 rounded-xl p-4 mb-3` }, [
        h('div', { class: `font-display font-bold mb-2 text-${color}-300` }, titulo),
        h('div', { class: 'space-y-2' }, [
          h('div', {}, [
            h('label', { class: 'text-xs text-ink-300 block mb-1' }, '📱 Para iPhone (webcal://)'),
            h('div', { class: 'flex gap-2' }, [
              h('input', { value: urls.webcal, readonly: true, class: 'flex-1 bg-base-900 border border-base-700 rounded px-2 py-1 text-xs font-mono', onclick: (e) => e.target.select() }),
              h('button', { class: 'text-xs px-2 py-1 bg-gold-400 text-base-900 rounded', onclick: () => { navigator.clipboard.writeText(urls.webcal); toast('Copiado', 'success'); } }, 'Copiar'),
            ]),
          ]),
          h('div', {}, [
            h('label', { class: 'text-xs text-ink-300 block mb-1' }, '🌐 Para Google Calendar (https://)'),
            h('div', { class: 'flex gap-2' }, [
              h('input', { value: urls.https, readonly: true, class: 'flex-1 bg-base-900 border border-base-700 rounded px-2 py-1 text-xs font-mono', onclick: (e) => e.target.select() }),
              h('button', { class: 'text-xs px-2 py-1 bg-gold-400 text-base-900 rounded', onclick: () => { navigator.clipboard.writeText(urls.https); toast('Copiado', 'success'); } }, 'Copiar'),
            ]),
          ]),
        ]),
      ]);
      return wrap;
    };
    body.appendChild(block('📓 Calendario Personal', r.personal, 'gold'));
    body.appendChild(block('🤖 Calendario IA (agentes)', r.ai, 'blue'));
    body.appendChild(block('📅 Todo junto', r.all, 'emerald'));
    body.appendChild(h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3 text-xs text-ink-200' }, [
      h('div', { class: 'font-bold mb-1' }, '📲 Pasos en iPhone 13 Pro Max:'),
      h('ol', { class: 'list-decimal pl-4 space-y-0.5' }, r.instructions.map((s) => h('li', {}, s))),
    ]));
    body.appendChild(h('button', { type: 'button', class: 'w-full mt-4 bg-base-700 hover:bg-base-600 text-ink-100 px-4 py-2 rounded-lg text-sm transition', onclick: () => overlay.remove() }, 'Cerrar'));
  }, 100);
}

// ============================ LIST VIEW ============================
function renderListView(container, all, root) {
  // Filtros
  container.appendChild(h('div', { class: 'flex flex-wrap gap-2 mb-4' }, [
    h('input', { type: 'search', placeholder: '🔍 Buscar...', value: _filter.q,
      class: 'flex-1 min-w-[180px] bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      oninput: (e) => { _filter.q = e.target.value; renderTareas(root); } }),
    h('select', {
      class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => { _filter.seccion = e.target.value; renderTareas(root); },
    }, [
      h('option', { value: '' }, 'Todas las secciones'),
      ...getSecs().map((s) => {
        const opt = document.createElement('option');
        opt.value = s.id; opt.textContent = `${s.emoji} ${s.label}`;
        if (_filter.seccion === s.id) opt.selected = true;
        return opt;
      }),
    ]),
    h('select', {
      class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-2 text-sm',
      onchange: (e) => { _filter.status = e.target.value; renderTareas(root); },
    }, [
      ['pending', '🔥 Pendientes'], ['done', '✓ Completadas'], ['all', 'Todas'],
    ].map(([v, l]) => {
      const opt = document.createElement('option');
      opt.value = v; opt.textContent = l;
      if (_filter.status === v) opt.selected = true;
      return opt;
    })),
  ]));

  // Barra acciones masivas (solo si hay selección)
  if (_selected.size > 0) {
    container.appendChild(h('div', { class: 'sticky top-2 z-10 flex items-center justify-between gap-2 mb-4 bg-gold-500/20 backdrop-blur border-2 border-gold-400 rounded-xl px-4 py-3 shadow-lg' }, [
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { class: 'text-2xl' }, '✅'),
        h('div', { class: 'text-base font-bold text-gold-100' }, _selected.size + ' tarea(s) seleccionada(s)'),
      ]),
      h('div', { class: 'flex gap-2 flex-wrap' }, [
        h('button', { class: 'text-sm bg-base-800 hover:bg-base-700 border border-base-600 rounded-lg px-4 py-2 text-ink-100', onclick: () => { _selected = new Set(); renderTareas(root); } }, '✕ Quitar selección'),
        h('button', { class: 'text-sm bg-rose-600 hover:bg-rose-500 text-white rounded-lg px-4 py-2 font-bold shadow', onclick: async () => {
          const ids = Array.from(_selected);
          const ok = await openConfirm({ title: 'Eliminar ' + ids.length + ' tareas', message: 'Esta acción no se puede deshacer', danger: true, icon: '🗑️' });
          if (!ok) return;
          let okCount = 0, failCount = 0;
          for (const id of ids) {
            try { await apiClient.delete('/api/v1/tasks/' + id); okCount++; } catch(e) { failCount++; }
          }
          _selected = new Set();
          toast(okCount + ' eliminadas' + (failCount ? ' · ' + failCount + ' fallaron' : ''), failCount ? 'warning' : 'success');
          renderTareas(root);
        } }, '🗑️ Eliminar ' + _selected.size),
      ]),
    ]));
  }

  registerSecsFromTasks(all);
  // Filtrar
  const filtered = all.filter((t) => {
    if (_filter.seccion && t.seccion !== _filter.seccion) return false;
    if (_filter.status === 'pending' && t.status === 'done') return false;
    if (_filter.status === 'done' && t.status !== 'done') return false;
    if (_filter.q) {
      const q = _filter.q.toLowerCase();
      if (![t.title, t.description].some((x) => (x || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  if (filtered.length === 0) {
    container.appendChild(emptyState('✅', 'Nada por aquí', 'Crea tu primera tarea o cambia los filtros.', btn('+ Nueva tarea', () => openTaskModal(null, () => renderTareas(root)))));
    return;
  }
  // Agrupar por sección
  const grouped = {};
  for (const t of filtered) (grouped[t.seccion || 'personal'] = grouped[t.seccion || 'personal'] || []).push(t);
  for (const s of getSecs()) {
    const items = grouped[s.id];
    if (!items || items.length === 0) continue;
    const withSched = items.filter(hasSchedule).sort((a, b) => (a.startAt || a.dueAt || 0) - (b.startAt || b.dueAt || 0));
    const general = items.filter((t) => !hasSchedule(t));
    container.appendChild(h('section', { class: 'mb-6' }, [
      h('div', { class: 'flex items-center justify-between mb-3 pb-2 border-b border-base-700/50' }, [
        h('div', { class: 'flex items-center gap-2' }, [
          h('span', { class: 'text-2xl' }, s.emoji),
          h('h3', { class: 'font-display font-semibold text-lg' }, s.label),
          h('span', { class: 'text-xs text-ink-400' }, `· ${items.length}`),
        ]),
        h('button', { class: 'text-xs text-gold-400 hover:text-gold-300',
          onclick: () => openQuickTaskModal(s.id, () => renderTareas(root)) }, '+ Añadir aquí'),
      ]),
      withSched.length > 0 ? h('div', { class: 'mb-3' }, [
        h('div', { class: 'text-xs uppercase tracking-wider text-blue-400 mb-2 font-semibold' }, `🕐 Con horario (${withSched.length})`),
        h('div', { class: 'space-y-2' }, withSched.map((t) => taskCard(t, root))),
      ]) : null,
      general.length > 0 ? h('div', {}, [
        h('div', { class: 'text-xs uppercase tracking-wider text-purple-400 mb-2 font-semibold' }, `📌 Generales (${general.length})`),
        h('div', { class: 'space-y-2' }, general.map((t) => taskCard(t, root))),
      ]) : null,
    ]));
  }
}

// ============================ CALENDAR VIEW (estilo iPhone) ============================
function renderCalendarView(container, all, root) {
  // Navegación mes
  const monthNames = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const nav = h('div', { class: 'flex items-center justify-between mb-4' }, [
    h('button', { class: 'bg-base-800 hover:bg-base-700 border border-base-700 rounded-lg px-3 py-1.5 text-sm', onclick: () => { _calMonth.setMonth(_calMonth.getMonth() - 1); renderTareas(root); } }, '←'),
    h('div', { class: 'text-center' }, [
      h('div', { class: 'font-display font-bold text-xl' }, `${monthNames[_calMonth.getMonth()]} ${_calMonth.getFullYear()}`),
      h('button', { class: 'text-xs text-gold-400 hover:text-gold-300 mt-0.5', onclick: () => { _calMonth = new Date(); _calMonth.setDate(1); renderTareas(root); } }, 'Hoy'),
    ]),
    h('button', { class: 'bg-base-800 hover:bg-base-700 border border-base-700 rounded-lg px-3 py-1.5 text-sm', onclick: () => { _calMonth.setMonth(_calMonth.getMonth() + 1); renderTareas(root); } }, '→'),
  ]);
  container.appendChild(nav);

  // Grid weekday headers
  const weekdays = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  container.appendChild(h('div', { class: 'grid grid-cols-7 gap-1 mb-2' },
    weekdays.map((w) => h('div', { class: 'text-center text-xs font-bold text-ink-400 uppercase tracking-wider py-1' }, w))));

  // Calcular días
  const year = _calMonth.getFullYear();
  const month = _calMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  let firstWeekday = firstDay.getDay(); // 0=Sun
  firstWeekday = firstWeekday === 0 ? 6 : firstWeekday - 1; // L=0 ... D=6
  const lastDay = new Date(year, month + 1, 0).getDate();
  const today = new Date();
  const isToday = (d) => d.toDateString() === today.toDateString();

  // Agrupar tareas por día YYYY-MM-DD
  const byDay = {};
  for (const t of all) {
    const ts = t.startAt || t.dueAt;
    if (!ts) continue;
    const d = new Date(ts);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    (byDay[key] = byDay[key] || []).push(t);
  }

  // Pintar grid
  const grid = h('div', { class: 'grid grid-cols-7 gap-1' });
  // Espacios vacíos antes del día 1
  for (let i = 0; i < firstWeekday; i++) grid.appendChild(h('div', { class: 'aspect-square' }));
  for (let day = 1; day <= lastDay; day++) {
    const d = new Date(year, month, day);
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const tasks = (byDay[key] || []).sort((a, b) => (a.startAt || a.dueAt || 0) - (b.startAt || b.dueAt || 0));
    const isCurrent = isToday(d);
    grid.appendChild(h('div', {
      class: `aspect-square min-h-[80px] bg-base-800/40 border ${isCurrent ? 'border-gold-400/60 ring-1 ring-gold-400/30' : 'border-base-700/50'} rounded-lg p-1.5 hover:border-gold-400/40 cursor-pointer transition flex flex-col`,
      onclick: () => openDayDetail(d, tasks, root),
    }, [
      h('div', { class: `text-sm font-bold mb-0.5 ${isCurrent ? 'text-gold-300' : 'text-ink-200'}` }, String(day)),
      h('div', { class: 'flex-1 overflow-hidden flex flex-col gap-0.5' },
        tasks.slice(0, 3).map((t) => {
          const sec = getSecs().find((s) => s.id === t.seccion) || SECCIONES[0];
          const label = (t.startAt ? new Date(t.startAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) + ' ' : '') + t.title.slice(0, 16);
          return h('div', {
            class: `text-[10px] truncate rounded px-1 py-0.5 ${t.status === 'done' ? 'opacity-50 line-through' : ''} bg-${sec.color}-500/20 text-${sec.color}-200 border border-${sec.color}-500/30`,
            title: t.title,
          }, label);
        })),
      tasks.length > 3 ? h('div', { class: 'text-[9px] text-ink-400 mt-auto' }, `+${tasks.length - 3} más`) : null,
    ]));
  }
  container.appendChild(grid);

  // Leyenda
  container.appendChild(h('div', { class: 'flex flex-wrap gap-2 mt-4 text-xs text-ink-400' }, [
    h('span', {}, 'Click en un día para ver/añadir tareas'),
  ]));
}

function openDayDetail(date, tasks, root) {
  const overlay = h('div', {
    class: 'fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4',
    onclick: (e) => { if (e.target === overlay) overlay.remove(); },
  }, [
    h('div', { class: 'bg-base-900 border border-base-700 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto' }, [
      h('div', { class: 'p-5 border-b border-base-700 flex items-center justify-between sticky top-0 bg-base-900' }, [
        h('h3', { class: 'font-display font-bold text-lg' }, date.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long' })),
        h('button', { class: 'text-2xl text-ink-400', onclick: () => overlay.remove() }, '×'),
      ]),
      h('div', { class: 'p-5 space-y-2' },
        tasks.length === 0
          ? [h('p', { class: 'text-sm text-ink-400 text-center py-4' }, 'Sin tareas este día.')]
          : tasks.map((t) => taskCard(t, root))),
      h('div', { class: 'p-4 border-t border-base-700' }, [
        btn('+ Añadir tarea este día', () => {
          overlay.remove();
          const presetStart = new Date(date); presetStart.setHours(9, 0, 0, 0);
          openTaskModal({ startAt: presetStart.getTime() }, () => renderTareas(root));
        }),
      ]),
    ]),
  ]);
  document.body.appendChild(overlay);
}

// ============================ GENERAL VIEW (sin horario) ============================
function renderGeneralView(container, all, root) {
  const general = all.filter((t) => !hasSchedule(t) && t.status !== 'done');
  if (general.length === 0) {
    container.appendChild(emptyState('📌', 'Sin tareas generales', 'Todas tus tareas tienen horario o están completadas.'));
    return;
  }
  // Agrupar por sección
  const grouped = {};
  for (const t of general) (grouped[t.seccion || 'personal'] = grouped[t.seccion || 'personal'] || []).push(t);
  for (const s of getSecs()) {
    const items = grouped[s.id];
    if (!items || items.length === 0) continue;
    container.appendChild(h('section', { class: 'mb-6' }, [
      h('div', { class: 'flex items-center gap-2 mb-3' }, [
        h('span', { class: 'text-2xl' }, s.emoji),
        h('h3', { class: 'font-display font-semibold text-lg' }, s.label),
        h('span', { class: 'text-xs text-ink-400' }, `· ${items.length}`),
      ]),
      h('div', { class: 'space-y-2' }, items.map((t) => taskCard(t, root))),
    ]));
  }
}

// ============================ HELPERS ============================
function hasSchedule(t) { return !!(t.startAt || t.dueAt || t.endAt); }

function taskCard(t, root) {
  const isDone = t.status === 'done';
  const time = formatScheduleTime(t);
  const isSel = _selected.has(t.id);
  return h('div', { 'data-task-card': t.id, class: `bg-base-800/50 border ${isSel ? 'border-gold-400 bg-gold-500/5' : 'border-base-700 hover:border-gold-400/40'} rounded-xl p-3 transition group ${isDone ? 'opacity-60' : ''}` }, [
    h('div', { class: 'flex items-start gap-3' }, [
      h('label', { 'data-task-checkbox': t.id, class: 'task-checkbox mt-0.5 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer shrink-0 ' + (isSel ? 'bg-gold-400 border-2 border-gold-300' : 'border-2 border-base-500 hover:border-gold-400/60'),
        onclick: (e) => {
          e.stopPropagation();
          const nowSel = !_selected.has(t.id);
          if (nowSel) _selected.add(t.id); else _selected.delete(t.id);
          // Actualización visual local SIN re-render
          const label = e.currentTarget;
          if (nowSel) {
            label.className = 'task-checkbox mt-0.5 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer shrink-0 bg-gold-400 border-2 border-gold-300';
            label.innerHTML = '<span class="text-base-900 text-sm font-bold">✓</span>';
            label.closest('[data-task-card]').classList.add('border-gold-400','bg-gold-500/5');
            label.closest('[data-task-card]').classList.remove('border-base-700','hover:border-gold-400/40');
          } else {
            label.className = 'task-checkbox mt-0.5 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer shrink-0 border-2 border-base-500 hover:border-gold-400/60';
            label.innerHTML = '';
            label.closest('[data-task-card]').classList.remove('border-gold-400','bg-gold-500/5');
            label.closest('[data-task-card]').classList.add('border-base-700','hover:border-gold-400/40');
          }
          updateBulkBar(root);
        }
      }, isSel ? h('span', { class: 'text-base-900 text-sm font-bold' }, '✓') : null),
      h('button', {
        class: `task-tick mt-0.5 w-5 h-5 rounded border-2 ${isDone ? 'bg-emerald-500 border-emerald-500' : 'border-base-500 hover:border-gold-400'} flex items-center justify-center transition shrink-0`,
        onclick: async (e) => {
          e.stopPropagation();
          const btn = e.currentTarget;
          const card = btn.closest('[data-task-card]');
          // Leer estado actual desde el objeto (mutamos t.status, no la closure)
          const currentDone = t.status === 'done';
          const newDone = !currentDone;
          // Update visual INMEDIATO sin re-render
          if (newDone) {
            btn.className = 'task-tick mt-0.5 w-5 h-5 rounded border-2 bg-emerald-500 border-emerald-500 flex items-center justify-center transition shrink-0';
            btn.innerHTML = '<span class="text-white text-xs">✓</span>';
            card.classList.add('opacity-60');
            const titleEl = card.querySelector('.flex-1 .font-medium');
            if (titleEl) { titleEl.classList.add('line-through','text-ink-400'); titleEl.classList.remove('text-ink-100'); }
          } else {
            btn.className = 'task-tick mt-0.5 w-5 h-5 rounded border-2 border-base-500 hover:border-gold-400 flex items-center justify-center transition shrink-0';
            btn.innerHTML = '';
            card.classList.remove('opacity-60');
            const titleEl = card.querySelector('.flex-1 .font-medium');
            if (titleEl) { titleEl.classList.remove('line-through','text-ink-400'); titleEl.classList.add('text-ink-100'); }
          }
          // Mutar también el objeto para que el siguiente click lea el estado correcto
          t.status = newDone ? 'done' : 'todo';
          t.completedAt = newDone ? Date.now() : null;
          // Llamada API en background
          try {
            await apiClient.patch(`/api/v1/tasks/${t.id}`, { status: newDone ? 'done' : 'todo', completedAt: newDone ? Date.now() : null });
            toast(newDone ? 'Completada ✓' : 'Marcada pendiente', 'success');
          } catch(err) {
            toast('Error guardando', 'error');
          }
        },
      }, isDone ? h('span', { class: 'text-white text-xs' }, '✓') : null),
      h('div', { class: 'flex-1 min-w-0 cursor-pointer', onclick: () => openTaskModal(t, () => renderTareas(root)) }, [
        h('div', { class: 'flex items-start justify-between gap-2' }, [
          h('div', { class: `font-medium ${isDone ? 'line-through text-ink-400' : 'text-ink-100'}` }, t.title),
          h('span', { class: `text-[10px] px-1.5 py-0.5 rounded border ${PRIO_COLORS[t.priority] || PRIO_COLORS.media} font-semibold shrink-0` }, PRIO_LABELS[t.priority] || 'P2'),
        ]),
        t.description ? h('p', { class: 'text-xs text-ink-300 mt-1 line-clamp-2' }, t.description) : null,
        h('div', { class: 'flex items-center gap-2 mt-1.5 text-xs flex-wrap' }, [
          time ? h('span', { class: 'inline-flex items-center gap-1 bg-blue-500/15 text-blue-300 border border-blue-400/30 rounded-full px-2 py-0.5' }, ['🕐', time]) : null,
        ].filter(Boolean)),
      ]),
      h('button', {
        class: 'opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-300 text-sm shrink-0',
        onclick: async (e) => {
          e.stopPropagation();
          const btn = e.currentTarget;
          const card = btn.closest('[data-task-card]');
          try {
            await apiClient.delete(`/api/v1/tasks/${t.id}`);
            // Fade out + remove
            card.style.transition = 'opacity 0.25s, transform 0.25s';
            card.style.opacity = '0';
            card.style.transform = 'scale(0.95)';
            setTimeout(() => card.remove(), 250);
            _selected.delete(t.id);
            updateBulkBar(root);
            toast('Tarea eliminada', 'success');
          } catch(err) { toast('Error', 'error'); }
        },
      }, '🗑'),
    ]),
  ]);
}

function formatScheduleTime(t) {
  const start = t.startAt;
  const end = t.endAt;
  if (!start && !t.dueAt) return null;
  const dStart = new Date(start || t.dueAt);
  const now = new Date();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const sameDay = dStart.toDateString() === now.toDateString();
  const isTomorrow = dStart.toDateString() === tomorrow.toDateString();
  const timeStr = dStart.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  let prefix = '';
  if (sameDay) prefix = 'Hoy';
  else if (isTomorrow) prefix = 'Mañana';
  else prefix = dStart.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  let result = `${prefix} ${timeStr}`;
  if (end) {
    const dEnd = new Date(end);
    const endTime = dEnd.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    result += ` – ${endTime}`;
  }
  return result;
}

function openTaskModal(task, onDone) {
  const start = task?.startAt ? toLocalInput(task.startAt) : '';
  const end = task?.endAt ? toLocalInput(task.endAt) : '';
  openModal({
    title: task?.id ? 'Editar tarea/evento' : 'Nueva tarea/evento',
    icon: '📅',
    size: 'lg',
    fields: [
      { name: 'title', label: 'Título', required: true, value: task?.title, placeholder: '¿Qué tienes que hacer?' },
      { name: 'seccion', label: 'Sección', type: 'select', value: task?.seccion || 'personal',
        options: getSecs().map((s) => ({ value: s.id, label: `${s.emoji} ${s.label}` })) },
      { name: 'priority', label: 'Prioridad', type: 'select', value: task?.priority || 'media', options: [
        { value: 'critica', label: 'P0 — Urgente' },
        { value: 'alta', label: 'P1 — Alta' },
        { value: 'media', label: 'P2 — Media' },
        { value: 'baja', label: 'P3 — Baja' },
      ] },
      { name: 'allDay', label: '☀️ Todo el día', type: 'checkbox', value: !!task?.allDay },
      { name: 'startAt', label: '🕐 Inicio', type: 'datetime-local', value: start, help: 'Déjalo vacío para tarea sin hora' },
      { name: 'endAt', label: '🏁 Fin', type: 'datetime-local', value: end, help: 'Opcional · si es evento con duración' },
      { name: 'recurrence', label: '🔁 Repetir', type: 'select', value: task?.recurrence || '', options: [
        { value: '', label: 'Nunca' },
        { value: 'DAILY', label: 'Cada día' },
        { value: 'WEEKDAYS', label: 'Días laborables (L-V)' },
        { value: 'WEEKLY', label: 'Cada semana' },
        { value: 'BIWEEKLY', label: 'Cada 2 semanas' },
        { value: 'MONTHLY', label: 'Cada mes' },
        { value: 'YEARLY', label: 'Cada año' },
      ] },
      { name: 'location', label: '📍 Ubicación (opcional)', value: task?.location || '', placeholder: 'Calle, ciudad, lugar...' },
      { name: 'description', label: 'Notas', type: 'textarea', rows: 3, value: task?.description },
    ],
    onSubmit: async (vals) => {
      const payload = {
        title: vals.title,
        seccion: vals.seccion,
        priority: vals.priority,
        description: vals.description || null,
        startAt: vals.startAt ? new Date(vals.startAt).getTime() : null,
        endAt: vals.endAt ? new Date(vals.endAt).getTime() : null,
        recurrence: vals.recurrence || null,
        location: vals.location || null,
        allDay: vals.allDay ? 1 : 0,
        status: task?.status || 'todo',
      };
      if (task?.id) {
        await apiClient.patch(`/api/v1/tasks/${task.id}`, payload);
        toast('Tarea actualizada', 'success');
      } else {
        await apiClient.post('/api/v1/tasks', payload);
        toast('Tarea creada', 'success');
      }
      onDone();
    },
  });
}

function toLocalInput(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}


// Actualiza barra de selección masiva SIN re-renderizar
function updateBulkBar(root) {
  const existing = root.querySelector('[data-bulk-bar]');
  if (_selected.size === 0) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    const counter = existing.querySelector('[data-bulk-count]');
    const counter2 = existing.querySelector('[data-bulk-count2]');
    if (counter) counter.textContent = _selected.size + ' tarea(s) seleccionada(s)';
    if (counter2) counter2.textContent = '🗑️ Eliminar ' + _selected.size;
    return;
  }
  // Crear desde cero
  const bar = document.createElement('div');
  bar.setAttribute('data-bulk-bar', '');
  bar.className = 'sticky top-2 z-10 flex items-center justify-between gap-2 mb-4 bg-gold-500/20 backdrop-blur border-2 border-gold-400 rounded-xl px-4 py-3 shadow-lg';
  bar.innerHTML = `
    <div class="flex items-center gap-2">
      <span class="text-2xl">✅</span>
      <div data-bulk-count class="text-base font-bold text-gold-100">${_selected.size} tarea(s) seleccionada(s)</div>
    </div>
    <div class="flex gap-2 flex-wrap">
      <button data-bulk-clear class="text-sm bg-base-800 hover:bg-base-700 border border-base-600 rounded-lg px-4 py-2 text-ink-100">✕ Quitar selección</button>
      <button data-bulk-delete data-bulk-count2 class="text-sm bg-rose-600 hover:bg-rose-500 text-white rounded-lg px-4 py-2 font-bold shadow">🗑️ Eliminar ${_selected.size}</button>
    </div>`;
  // Insertar al inicio del primer hijo de root
  const firstChild = root.firstChild && root.firstChild.firstChild;
  if (firstChild) firstChild.insertBefore(bar, firstChild.firstChild);
  else root.insertBefore(bar, root.firstChild);
  // Handlers
  bar.querySelector('[data-bulk-clear]').onclick = () => {
    _selected.clear();
    root.querySelectorAll('.task-checkbox').forEach(cb => {
      cb.className = 'task-checkbox mt-0.5 flex items-center justify-center w-6 h-6 rounded-md cursor-pointer shrink-0 border-2 border-base-500 hover:border-gold-400/60';
      cb.innerHTML = '';
      const card = cb.closest('[data-task-card]');
      if (card) { card.classList.remove('border-gold-400','bg-gold-500/5'); card.classList.add('border-base-700','hover:border-gold-400/40'); }
    });
    bar.remove();
  };
  bar.querySelector('[data-bulk-delete]').onclick = async () => {
    const ids = Array.from(_selected);
    const ok = await openConfirm({ title: 'Eliminar ' + ids.length + ' tareas', message: 'Esta acción no se puede deshacer', danger: true, icon: '🗑️' });
    if (!ok) return;
    let okCount = 0, failCount = 0;
    for (const id of ids) {
      try {
        await apiClient.delete('/api/v1/tasks/' + id);
        okCount++;
        const card = root.querySelector(`[data-task-card="${id}"]`);
        if (card) { card.style.transition='opacity 0.25s'; card.style.opacity='0'; setTimeout(() => card.remove(), 250); }
      } catch(e) { failCount++; }
    }
    _selected.clear();
    bar.remove();
    toast(okCount + ' eliminadas' + (failCount ? ' · ' + failCount + ' fallaron' : ''), failCount ? 'warning' : 'success');
  };
}

// =================== TAREAS POR SECCIÓN (Autosell, Inmuebles, ...) ===================
// Lista compacta de tareas filtradas por una sección. Sincronizada con Tareas principal.
export async function renderTasksForSection(container, seccion) {
  container.innerHTML = '';
  const SECTION_LABELS = { autosell: '🚗 Autosell', inmuebles: '🏘️ Inmuebles', personal: '🌟 Personal', salud: '💪 Salud' };
  const label = SECTION_LABELS[seccion] || seccion;

  const wrap = h('div', {}, []);
  container.appendChild(wrap);

  // Header
  const header = h('div', { class: 'flex items-center justify-between mb-4 flex-wrap gap-2' }, [
    h('div', {}, [
      h('h3', { class: 'font-display text-xl font-bold' }, '✅ Tareas · ' + label),
      h('p', { class: 'text-xs text-ink-300' }, 'Sincronizadas con la sección Tareas principal · solo las de ' + label),
    ]),
    h('button', {
      class: 'bg-gold-500 text-black font-semibold rounded-lg px-4 py-2 text-sm hover:bg-gold-400',
      onclick: () => {
        openTaskModal({ seccion }, () => renderTasksForSection(container, seccion));
      }
    }, '+ Nueva tarea'),
  ]);
  wrap.appendChild(header);

  let _localStatus = 'pending';
  const filterBar = h('div', { class: 'flex gap-2 mb-4 flex-wrap' });
  function refreshFilterBar() {
    filterBar.innerHTML = '';
    [
      { v: 'pending', l: '🔥 Pendientes' },
      { v: 'all',     l: '📋 Todas' },
      { v: 'done',    l: '✅ Hechas' },
    ].forEach((opt) => {
      filterBar.appendChild(h('button', {
        class: 'px-3 py-1.5 rounded-lg text-sm border ' + (_localStatus === opt.v
          ? 'bg-gold-500/20 border-gold-400 text-gold-200'
          : 'bg-base-800 border-base-700 text-ink-300 hover:border-gold-400/40'),
        onclick: () => { _localStatus = opt.v; refreshFilterBar(); reloadList(); }
      }, opt.l));
    });
  }
  refreshFilterBar();
  wrap.appendChild(filterBar);

  const listContainer = h('div', { class: 'space-y-2' });
  wrap.appendChild(listContainer);

  async function reloadList() {
    listContainer.innerHTML = '<div class="text-ink-300 text-sm py-4 text-center">Cargando...</div>';
    try {
      const r = await apiClient.get('/api/v1/tasks?limit=500');
      let tasks = (r.data || []).filter((t) => t.seccion === seccion);
      if (_localStatus === 'pending') tasks = tasks.filter((t) => t.status !== 'done');
      else if (_localStatus === 'done') tasks = tasks.filter((t) => t.status === 'done');

      listContainer.innerHTML = '';
      if (tasks.length === 0) {
        listContainer.appendChild(emptyState('✅', 'Sin tareas', 'Crea la primera con el botón de arriba.'));
        return;
      }
      tasks.forEach((t) => {
        listContainer.appendChild(taskCard(t, container));
      });
    } catch (e) {
      listContainer.innerHTML = '<div class="text-rose-300 text-sm py-4">Error cargando tareas</div>';
    }
  }
  await reloadList();
}
